import { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  Copy,
  ExternalLink,
  AlertCircle,
  Loader2,
  CheckCircle,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import { integrationsAPI } from '../../services/api';

export default function HubSpotSetupWizard({
  isOpen,
  onClose,
  onComplete,
  initialStep = 1,
  portalId: initialPortalId = null,
  isReconfigure = false
}) {
  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [portalId, setPortalId] = useState(initialPortalId || '');
  const [credentials, setCredentials] = useState(null);
  const [propertiesCreated, setPropertiesCreated] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const totalSteps = 4;

  // If we have initialPortalId (from OAuth callback), setup credentials
  useEffect(() => {
    if (isOpen && initialPortalId && initialStep === 2) {
      loadCredentials();
    }
  }, [isOpen, initialPortalId, initialStep]);

  useEffect(() => {
    if (isOpen && isReconfigure) {
      loadExistingConfig();
    }
  }, [isOpen, isReconfigure]);

  const loadExistingConfig = async () => {
    try {
      const response = await integrationsAPI.getConfig('hubspot');
      if (response.data.config) {
        setPortalId(response.data.config.portalId || '');
        setCredentials(response.data);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await integrationsAPI.setupHubSpot({
        portalId: initialPortalId
      });
      setCredentials(response.data);
      setPortalId(initialPortalId);
    } catch (error) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOAuth = async () => {
    setLoading(true);
    try {
      const response = await integrationsAPI.startHubSpotOAuth();
      // Redirect to HubSpot OAuth
      window.location.href = response.data.authorizationUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start OAuth');
      setLoading(false);
    }
  };

  const handleCreateProperties = async () => {
    setPropertiesLoading(true);
    try {
      await integrationsAPI.createHubSpotProperties();
      setPropertiesCreated(true);
      toast.success('Custom properties created!');
    } catch (error) {
      // Properties might already exist
      if (error.response?.status === 409) {
        setPropertiesCreated(true);
        toast.success('Properties already exist');
      } else {
        toast.error(error.response?.data?.error || 'Failed to create properties');
      }
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!callbackUrl.trim()) {
      toast.error('Please enter a callback URL to test');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    try {
      const response = await integrationsAPI.test('hubspot', {
        callbackUrl: callbackUrl.trim()
      });
      setTestResult({ success: true, message: response.data.message });
      toast.success('Test callback sent!');
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Test failed'
      });
      toast.error('Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const copyToClipboard = (text, label = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleComplete = () => {
    onComplete();
    toast.success('HubSpot integration setup complete!');
  };

  if (!isOpen) return null;

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <svg viewBox="0 0 24 24" fill="white" className="w-12 h-12">
            <path d="M17.44 11.06c-.24-.5-.6-.89-1.06-1.17-.47-.28-1.01-.42-1.62-.42-.6 0-1.14.14-1.6.42-.47.28-.83.67-1.08 1.17-.25.5-.38 1.08-.38 1.73s.13 1.23.38 1.73c.25.5.61.89 1.08 1.17.46.28 1 .42 1.6.42.61 0 1.15-.14 1.62-.42.46-.28.82-.67 1.06-1.17.24-.5.36-1.08.36-1.73s-.12-1.23-.36-1.73z"/>
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Connect Your HubSpot Account
        </h3>
        <p className="text-sm text-gray-500">
          Securely authorize Alfred Voice to access your HubSpot CRM
        </p>
      </div>

      <button
        onClick={handleStartOAuth}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-colors text-lg font-medium disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
              <path d="M17.44 11.06c-.24-.5-.6-.89-1.06-1.17-.47-.28-1.01-.42-1.62-.42-.6 0-1.14.14-1.6.42-.47.28-.83.67-1.08 1.17-.25.5-.38 1.08-.38 1.73s.13 1.23.38 1.73c.25.5.61.89 1.08 1.17.46.28 1 .42 1.6.42.61 0 1.15-.14 1.62-.42.46-.28.82-.67 1.06-1.17.24-.5.36-1.08.36-1.73s-.12-1.23-.36-1.73z"/>
            </svg>
            Connect with HubSpot
          </>
        )}
      </button>

      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-2">We'll request access to:</p>
            <ul className="space-y-1 text-gray-500">
              <li>• Read and write contacts</li>
              <li>• Read and write deals</li>
              <li>• Log call activities to timeline</li>
              <li>• Trigger from workflows</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
          <p className="text-gray-500">Loading credentials...</p>
        </div>
      ) : (
        <>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              HubSpot Connected!
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Portal ID: {portalId}
            </p>
          </div>

          {credentials && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">API Key</span>
                  <button
                    onClick={() => copyToClipboard(credentials.apiKey, 'API Key copied!')}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <code className="block w-full p-3 bg-white border border-gray-200 rounded text-sm font-mono break-all">
                  {credentials.apiKey}
                </code>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Portal ID</span>
                  <button
                    onClick={() => copyToClipboard(portalId, 'Portal ID copied!')}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <code className="block w-full p-3 bg-white border border-gray-200 rounded text-sm font-mono">
                  {portalId}
                </code>
              </div>
            </div>
          )}

          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium">Save your API key</p>
                <p>For security, we only show this once.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Create HubSpot Custom Properties
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Alfred Voice needs custom contact properties to store call data.
        </p>
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Properties to create:</h4>
        <div className="space-y-2">
          {[
            { name: 'alfred_last_call_date', type: 'Date' },
            { name: 'alfred_call_outcome', type: 'Dropdown' },
            { name: 'alfred_qualification_score', type: 'Number' },
            { name: 'alfred_budget_range', type: 'Text' },
            { name: 'alfred_timeline', type: 'Text' },
            { name: 'alfred_decision_maker', type: 'Checkbox' },
            { name: 'alfred_next_action', type: 'Dropdown' }
          ].map((prop) => (
            <div key={prop.name} className="flex items-center gap-3 text-sm">
              {propertiesCreated ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <div className="w-4 h-4 border border-gray-300 rounded" />
              )}
              <span className="font-mono text-gray-600">{prop.name}</span>
              <span className="text-gray-400">({prop.type})</span>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleCreateProperties}
        disabled={propertiesLoading || propertiesCreated}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
          propertiesCreated
            ? 'bg-green-100 text-green-700'
            : 'bg-black hover:bg-gray-800 text-white'
        } disabled:opacity-50`}
      >
        {propertiesLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Creating...
          </>
        ) : propertiesCreated ? (
          <>
            <CheckCircle className="w-4 h-4" />
            Properties Created
          </>
        ) : (
          'Create Properties Automatically'
        )}
      </button>

      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">This step is optional</p>
            <p>You can skip this and create properties manually in HubSpot later.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Configure HubSpot Workflow
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Add a webhook action in your HubSpot workflow with these settings:
        </p>
      </div>

      {credentials && (
        <div className="space-y-4">
          {/* Webhook URL */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">1. Webhook URL</span>
              <button
                onClick={() => copyToClipboard(credentials.webhookUrl)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <code className="block w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono break-all">
              {credentials.webhookUrl}
            </code>
          </div>

          {/* Headers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">2. Headers</span>
              <button
                onClick={() => copyToClipboard(
                  Object.entries(credentials.headers).map(([k, v]) => `${k}: ${v}`).join('\n'),
                  'Headers copied!'
                )}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy All
              </button>
            </div>
            <pre className="w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono overflow-x-auto">
{Object.entries(credentials.headers).map(([key, value]) => (
  `${key}: ${value}`
)).join('\n')}
            </pre>
          </div>

          {/* Payload Template */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">3. Payload (JSON Body)</span>
              <button
                onClick={() => copyToClipboard(
                  JSON.stringify(credentials.payloadTemplate, null, 2),
                  'Payload copied!'
                )}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <pre className="w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono overflow-x-auto max-h-48">
{JSON.stringify(credentials.payloadTemplate, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Test Section */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Callback URL (optional)
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            placeholder="https://your-callback-url.com"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
          />
          <button
            onClick={handleTestConnection}
            disabled={testLoading || !callbackUrl.trim()}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
          </button>
        </div>

        {testResult && (
          <div className={`mt-2 p-3 rounded-lg text-sm ${
            testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {testResult.success ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {testResult.message}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {testResult.message}
              </div>
            )}
          </div>
        )}
      </div>

      <a
        href="https://docs.alfredvoice.ai/integrations/hubspot"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <ExternalLink className="w-4 h-4" />
        View detailed setup guide
      </a>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                  <path d="M17.44 11.06c-.24-.5-.6-.89-1.06-1.17-.47-.28-1.01-.42-1.62-.42-.6 0-1.14.14-1.6.42-.47.28-.83.67-1.08 1.17-.25.5-.38 1.08-.38 1.73s.13 1.23.38 1.73c.25.5.61.89 1.08 1.17.46.28 1 .42 1.6.42.61 0 1.15-.14 1.62-.42.46-.28.82-.67 1.06-1.17.24-.5.36-1.08.36-1.73s-.12-1.23-.36-1.73z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isReconfigure ? 'Reconfigure' : 'Connect'} HubSpot
                </h2>
                <p className="text-sm text-gray-500">
                  Step {step} of {totalSteps}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 pt-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-orange-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50 rounded-b-2xl">
            <button
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            {step === 1 && (
              <div className="text-sm text-gray-500">
                Click the button above to connect
              </div>
            )}

            {step === 2 && !loading && (
              <button
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 3 && (
              <button
                onClick={() => setStep(4)}
                className="flex items-center gap-2 px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                {propertiesCreated ? 'Continue' : 'Skip for now'}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 4 && (
              <button
                onClick={handleComplete}
                className="flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
              >
                <Check className="w-4 h-4" />
                Complete Setup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
